import ResultBlock from './oc-result-block';
import {
  Name,
  ItemNumber,
  Distance,
  Address,
  MobileSwitch,
  Divider,
  ActionLink,
  Notes,
  MainCta,
} from './result-block-styled-components';
import { If } from '../../Utilities';
import { connectLocation } from '../../location/LocationConnectors';
import IconMapMarker from '../icons/icon-map-marker';
import Button from '../oc-button';

const typeFormatter = (type) => type?.replace('_', '-').trim()
const telFormatter = (phone) => `tel:${phone?.replaceAll(/\(|\s/g,'').replaceAll(/\)/g,'-')}`
const distanceFormatter = (distance, t, units) => t(`generic_locator.${units}_away`, {distance: Math.round(distance)})
const telDisplayFormatter = (phone) => {
  let phoneNumber = phone ? phone?.match(/\d+/g).join('') : ""
  let sectioned = phoneNumber?.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (sectioned) {
    return '(' + sectioned[1] + ') ' + sectioned[2] + '-' + sectioned[3];
  } else {
    return phoneNumber
  }
}

const fixUrl = (url) => {
  if (!url || url.trim() === "") {
      return null;
  }

  if (/^https?:\/\//.test(url)) {
      return url;
  }

  return `http://${url}`;
}

const Titelize = (str) => {
  // Split the string into words
  const words = str.split(' ');

  // Capitalize each word except for abbreviations
  const capitalizedWords = words.map(word => {
    if (word.toUpperCase() === word) {
      // Word is all uppercase, likely an abbreviation
      return word;
    } else {
      // Capitalize the first letter
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
  });

  // Join the words back into a string
  return capitalizedWords.join(' ');
}

export const ProfessionalResultBlock = connectLocation(({
  t,
  resource,
  dataProfessional,
  websiteCTALabel,
  emailCTALabel,
  directionsCTALabel,
  mainCTALabel,
  units = 'miles', // note: "miles" actually means: use the prior behavior of locale driven units. new behavior uses the abbreviated {"mi", "km"}
}) => {
  const website = resource.website_address || resource.website
  const combinedAddress = resource.address_2 ? (resource.address + ', ' + resource.address_2) : resource.address
  // state is optional for some european countries
  const fullAddress = `${combinedAddress}, ${resource.city}, ${resource.state || '' } ${resource.zip || resource.postal}`;

  return (
    <ResultBlock media={resource.logo} alt={resource.name}>
      <div>
        <Name font="body" className="name">
          {resource.list_number && <ItemNumber>{resource.list_number}.</ItemNumber>}
          <span dangerouslySetInnerHTML={{__html: Titelize(resource.name)}} />
        </Name>
        <Distance>{distanceFormatter(resource.distance, t, units)}</Distance>

        <Address>{fullAddress}</Address>

        <MobileSwitch>
          <div>
            <If condition={fullAddress}>
              <ActionLink
                href={`http://maps.google.com/?q=${fullAddress}`}
                tabIndex={0}
                data-track="view-directions"
                data-track-buy-type={typeFormatter(resource.typeDisplayName)}
                data-track-supplier-name={resource.name}
              >
                <IconMapMarker width="13" fill="currentColor" className="icon" />{directionsCTALabel ? directionsCTALabel : t('generic_locator.directions')}
              </ActionLink>
            </If>
            <If condition={fullAddress && (resource.phone || resource.email || website)}>
              <Divider>|</Divider>
            </If>
            <If condition={resource.phone}>
              <ActionLink
                href={telFormatter(resource.phone)}
                tabIndex={0}
                data-track="phone"
                data-track-buy-type={typeFormatter(resource.typeDisplayName)}
                data-track-supplier-name={resource.name}
              >
                {telDisplayFormatter(resource.phone)}
              </ActionLink>
            </If>
            <If condition={resource.phone && resource.email}>
              <Divider>|</Divider>
            </If>
            <If condition={resource.email}>
              <ActionLink
                href={`mailto:${resource.email}`}
                target="_blank"
                rel="noopener"
                tabIndex={0}
                data-track="email"
                data-track-buy-type={typeFormatter(resource.typeDisplayName)}
                data-track-supplier-name={resource.email}
              >
                {emailCTALabel ? emailCTALabel : t('generic_locator.email')}
              </ActionLink>
            </If>
            <If condition={website && (resource.email || resource.phone)}>
              <Divider>|</Divider>
            </If>
            <If condition={website}>
              <ActionLink
                href={fixUrl(website)}
                target="_blank"
                rel="noopener"
                tabIndex={0}
                data-track="website"
                data-track-buy-type={typeFormatter(resource.typeDisplayName)}
                data-track-supplier-name={resource.name}
              >
                { websiteCTALabel ? websiteCTALabel : t('generic_locator.website')}
              </ActionLink>
            </If>

            { resource.product_stock_display_text && <Notes dangerouslySetInnerHTML={{ __html: resource.product_stock_display_text }} /> }
          </div>
        </MobileSwitch>
      </div>

      <If condition={resource.phone}>
        <MainCta>
          <Button
            href={telFormatter(resource.phone)}
            data-track="phone"
            data-track-buy-type={typeFormatter(resource.typeDisplayName)}
            data-track-supplier-name={resource.name}
          >
            {mainCTALabel ? mainCTALabel : t('generic_locator.call_now')}
          </Button>
        </MainCta>
      </If>
    </ResultBlock>
)});

ProfessionalResultBlock.WrappedComponent.displayName = 'ProfessionalResultBlock';
